<template>
    <ContentComp :load-state="LoadState">
        <AlertMessage v-if="stationDatasource.source?.length === 0">{{ $tm('addtimepenalty.missingstationswithkm') }}</AlertMessage>
        <FormLayout>
            <FormLayoutRow class="mt-4">
                <FormLayoutLabel>
                    {{ $tm('addtimepenalty.reason') }}
                </FormLayoutLabel>
                <TypeAhead
                    v-if="timePenaltyTypes.length > 0"
                    class="flex-fill"
                    ref="typeahead"
                    placeholder=""
                    v-model:model-value="model.Reason!"
                    :datasources="[computedTypeaheadDatasource]"
                    :minimum-length="1"
                    :display-selected-text="true"
                    :hideNomatchText="true"
                    :autoresize="true"
                    @item-selected="itemSelected"
                ></TypeAhead>
                <InputText
                    v-else
                    v-model="model.Reason"
                    class="flex-fill"
                />
            </FormLayoutRow>

            <FormLayoutRow>
                <FormLayoutLabel>
                    {{ $tm('addtimepenalty.point') }}
                </FormLayoutLabel>
                <InputSelect
                    ref="stationroleref"
                    v-model="model.StationRoleID"
                    :datasource="stationDatasource"
                />
            </FormLayoutRow>

            <FormLayoutRow>
                <FormLayoutLabel>
                    {{ $tm('addtimepenalty.penalty') }}
                </FormLayoutLabel>
                <InputTimespan
                    v-model="model.Penalty"
                    icon=""
                    format="TIME"
                />
            </FormLayoutRow>
        </FormLayout>
    </ContentComp>
</template>

<script setup lang="ts">
import { ContentComp, InputTimespan, InputSelect, TypeAhead } from '@/components';
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import { useLoaders, useTaskLoader } from '@/composeables';
import type { ISelectLineDatasource } from '@/objects/ISelectLineDatasource';
import { useEventCommonStore, useEventCommonStoreRef } from '@/stores';
import { computed, nextTick, onMounted, ref, toRaw } from 'vue';
import { SearchRequest, TimepenaltyType, TypeAheadItem, TypeAheadResponse, type TimepenaltyRequest } from '@/objects/generated';
import InputText from '@/components/Basic/InputText.vue';
import AlertMessage from '@/components/Alert/AlertMessage.vue';
import { TimepenaltyDao } from '@/dao';
import { TypeAheadDataSource } from '@/components/Typeahead';
import { TimeSpan } from '@/objects/TimeSpan';
import { millisecondsToFormattedTime } from '@/utilities/dateutils';

const model = defineModel<TimepenaltyRequest>({ required: true });

const stationroleref = ref<HTMLInputElement | null>(null);

// Preload necessary data
const { AddLoader, LoadState } = useTaskLoader();
useLoaders(AddLoader, ['STATIONS', 'EVENT']);

const eventCommonStore = useEventCommonStore();
const { Event, StationRoles } = useEventCommonStoreRef();

const timePenaltyTypes = ref<TimepenaltyType[]>([]);

/**
 * Set up the station datasource
 */
const stationDatasource = computed<ISelectLineDatasource>(() => {
    let datasource: ISelectLineDatasource = { source: [] };

    let _roles = toRaw(StationRoles.value);
    _roles.sort((a, b) => ((a.Km ?? 0) > (b.Km ?? 0) ? -1 : 1)); // Reverse sorting, last km first

    let stationRoles = _roles
        .filter((x) => x.Km != null && (x.RaceID == null || x.RaceID == model.value.RaceID))
        .map((s) => {
            return {
                value: s.ID,
                text: `${s.Name} (${s.Km} km)`,
            };
        });

    // Sort the stations by name
    datasource.source = stationRoles;

    return datasource;
});

const computedTypeaheadDatasource = computed(
    () =>
        new TypeAheadDataSource('Contains', async (query: SearchRequest) =>
            TypeAheadResponse.FromJsValues({
                Items: timePenaltyTypes.value
                    .filter((x) => x.Name?.toLocaleLowerCase().includes((query.Query ?? '').toLocaleLowerCase()))
                    .map((x) =>
                        TypeAheadItem.FromJsValues({
                            ID: x.ID,
                            Text: x.Name,
                            Description:
                                (x.DefaultPenalty < 0 ? '-' : '+') + millisecondsToFormattedTime((x.DefaultPenalty ?? 0) * 1000, 'TIME'),
                        }),
                    ),
            }),
        ),
);

/**
 * Whenever we select a item from the typeahead
 */
const itemSelected = (item: TypeAheadItem) => {
    // We find the actual item from the datasource
    let selectedItem = timePenaltyTypes.value.find((x) => x.ID == item.ID);

    model.value.ReasonID = selectedItem?.ID ?? 0;
    model.value.Penalty = TimeSpan.FromSeconds(selectedItem?.DefaultPenalty ?? 0);

    // We focus the next input
    nextTick(() => stationroleref.value?.focus());
};

onMounted(async () => {
    await eventCommonStore.EnsureLoaded(['Event']);

    await AddLoader(async () => {
        // We need to load the stations
        timePenaltyTypes.value = await TimepenaltyDao.GetTimePenaltyTypesForSport(Event.value?.SportID ?? 0);
    }, 'loading.timepenaltytypes');

    // We try and set the default km to the first station in the list wich is the last km
    if (
        model.value.StationRoleID == null &&
        stationDatasource.value != undefined &&
        stationDatasource.value.source != undefined &&
        stationDatasource.value.source.length > 0
    ) {
        model.value.StationRoleID = stationDatasource.value.source[0].value as number;
    }
});
</script>
